import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { listAgentsSummary, listMultiAgentPrompts, AgentSummary } from "../api";

/**
 * Every agent a workflow node can point at: standard agents plus multi-agent
 * prompts, deduped by agent_id. Shared by the Studio and the New Workflow
 * dialog so both offer exactly the same choices.
 */
export function useWorkflowAgents() {
  const { data: standardAgents = [], isLoading: loadingStandard } = useQuery({
    queryKey: ["agents", "summary"],
    queryFn: listAgentsSummary,
  });

  const { data: multiAgents = [], isLoading: loadingMulti } = useQuery({
    queryKey: ["multi-agents", "summary"],
    queryFn: listMultiAgentPrompts,
  });

  const agents = useMemo(() => {
    const list: AgentSummary[] = [...standardAgents];
    // Multi-agent prompts expose their id as `id`; map it onto agent_id.
    multiAgents.forEach((ma: any) => {
      list.push({
        agent_id: ma.id || ma.agent_id,
        name: ma.name || ma.agent_name || "Multi-Agent Prompt",
        agent_name: ma.agent_name,
      });
    });
    const unique = new Map<string, AgentSummary>();
    list.forEach((a) => {
      if (a.agent_id && !unique.has(a.agent_id)) unique.set(a.agent_id, a);
    });
    return Array.from(unique.values());
  }, [standardAgents, multiAgents]);

  return { agents, isLoading: loadingStandard || loadingMulti };
}

export const agentDisplayName = (agent?: AgentSummary) =>
  agent?.name || agent?.agent_name || agent?.agent_id || "";
